import React from "react";
import Grid from "@mui/material/Grid";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import makeStyles from "@mui/styles/makeStyles";
import { Box, Chip } from "@mui/material";
import Typography from "@limitless/modern/data-display/typography/Typography";
import { useTranslation } from "@limitless/common/hooks";
import { useExpertOnboardingContext, useExpertOnboardingBrandLearningContentHook } from "@limitless/expert";
import { useNavigation } from "@limitless/portal";
import ExpertPracticeQuestionsOverview from "../components/onboarding/ExpertPracticeQuestionsOverview";
import ExpertMemberDetailsOverview from "../components/onboarding/member-details/ExpertMemberDetailsOverview";
import OnboardingContainer from "../components/containers/onboarding-container/OnboardingContainer";
import ExpertBrandLearningScreen from "./ExpertBrandLearningScreen";
import OnboardingConversation from "../components/onboarding/OnboardingConversation";
import ExpertOnboardingIdVerificationOverview from "../components/onboarding/ExpertOnboardingIdVerificationOverview";
import ExpertDocumentVerificationOverview from "../components/onboarding/ExpertDocumentVerificationOverview";
import { useLocation, useHistory } from "react-router-dom";
import ArrowRight from "@limitless/icons/ArrowRight";
import GraduationCap from "@limitless/icons/GraduationCap";
import OnboardingProgressCard from "./OnboardingProgressCard";

const useStyles = makeStyles(theme => ({
    tabContainer: {
        borderRadius: 9,
        paddingTop: "40px",
        overflowY: "auto",
        overflowX: "hidden",
        height: "100%",

        [theme.breakpoints.down("sm")]: {
            overflowY: "visible",
            height: "auto"
        }
    },
    tab: {
        minWidth: 120,
        marginTop: 12,
        color: theme.palette.text.secondary,
        letterSpacing: 1.05,
        textTransform: "none",
        marginLeft: "15px",
        paddingBottom: "25px"
    },
    row2Text: {
        display: "flex",
        alignItems: "center",
        color: theme.palette.primary.main
    },
    labelBox: {
        display: "flex",
        alignItems: "center",
        maxWidth: 180,
        marginRight: 10
    },
    defaultIcon: {
        color: theme.palette.info.light,
        width: "68px",
        height: "60px",
        opacity: "0.3",
        marginLeft: "28px"
    }
}));

const OnboardingDetailScreen = () => {
    const [tabId, setTabId] = React.useState(0);
    const [showBrandLearningPage, setShowBrandLearningPage] = React.useState(false);
    const { t } = useTranslation();
    const classes = useStyles();
    const { navigate } = useNavigation();
    const history = useHistory();
    const { onboardingDetail, hasActiveQuestions, onboardingSummary } = useExpertOnboardingContext();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const tabKey = queryParams.get("tab");
    const {
        loading,
        onboarding: {
            groupName,
            totalChecks,
            language,
            country,
            brandLearningPageEnabled,
            tenantName,
            documentVerification,
            displayName
        }
    } = onboardingDetail;

    const onboardingOutcomeState =
        onboardingSummary?.completed?.find(item => item.groupName === groupName)?.onboardingOutcomeState ?? null;

    const { fetchBrandLearningContent, onboardingBrandLearningContent } =
        useExpertOnboardingBrandLearningContentHook(false);
    const { message } = useExpertOnboardingContext();

    React.useEffect(() => {
        if (!groupName) {
            navigate("/onboarding");
        }
    }, []);

    const handleChangeTab = (event, newValue) => {
        setTabId(newValue);
        const selectedTab = tabs[newValue];
        const searchParams = new URLSearchParams(location.search);
        searchParams.set("tab", selectedTab.id);
        history.replace({ pathname: location.pathname, search: searchParams.toString() });
    };

    const handleBrandRulesClick = () => {
        fetchBrandLearningContent({ groupName, tenantName, language, country, brandLearningPageEnabled });
        setShowBrandLearningPage(true);
    };

    const hasChecks = totalChecks > 0 || false;

    const TABS = React.useMemo(
        () => [
            {
                label: `${t("Practice Tasks")}`,
                id: "practiceTasksTab",
                userVisibility: hasActiveQuestions,
                component: <ExpertPracticeQuestionsOverview />
            },
            {
                label: `${t("Extra Details")}`,
                id: "memberDetailsTab",
                userVisibility: hasChecks,
                component: <ExpertMemberDetailsOverview />
            },
            {
                label: `${t("ID Verification")}`,
                id: "idVerificationTab",
                userVisibility: true,
                component: <ExpertOnboardingIdVerificationOverview />
            },
            {
                label: `${t("Document Verification")}`,
                id: "documentVerificationTab",
                userVisibility: documentVerification,
                component: <ExpertDocumentVerificationOverview onboardingDetail={onboardingDetail} />
            }
        ],
        [hasActiveQuestions, documentVerification, hasChecks]
    );

    const tabs = React.useMemo(() => TABS.filter(tab => tab.userVisibility), [TABS]);

    React.useEffect(() => {
        if (tabKey) {
            const tabIndex = tabs.findIndex(item => item.id === tabKey);
            if (tabIndex !== -1) {
                setTabId(tabIndex);
            }
        }
    }, [tabKey, tabs]);

    const tab = React.useMemo(() => tabs[tabId].component, [tabId, tabs, onboardingDetail]);

    return (
        <>
            {!showBrandLearningPage && message?.groupName !== groupName && (
                <OnboardingContainer loading={loading} disableTitle>
                    <Grid container spacing={2} sx={{ height: "calc(100vh - 211px)", px: 2 }}>
                        <Grid item xs={12} md={9} display="flex" flexDirection="column">
                            <Grid container justifyContent="space-between" alignItems="center" mt={3}>
                                <Grid item>
                                    <Grid container direction="row" alignItems="center">
                                        <Typography fontSize={20} fontWeight={500}>
                                            {"Onboarding for"}
                                        </Typography>
                                        <Typography fontSize={20} fontWeight={500} marginLeft={1}>
                                            {" "}
                                            {`${displayName}`}
                                        </Typography>
                                    </Grid>
                                </Grid>

                                {(onboardingOutcomeState === "rejected" || onboardingOutcomeState === "promoted") && (
                                    <Chip
                                        label={
                                            onboardingOutcomeState === "rejected"
                                                ? t("Onboarding rejected")
                                                : onboardingOutcomeState === "promoted"
                                                ? t("Onboarding successful")
                                                : null
                                        }
                                        sx={
                                            onboardingOutcomeState === "promoted"
                                                ? { backgroundColor: theme => theme.palette.secondary.light }
                                                : { backgroundColor: theme => theme.palette.error.light }
                                        }
                                    />
                                )}
                            </Grid>

                            <Tabs value={tabId} onChange={handleChangeTab}>
                                {tabs.map((tab, index) => (
                                    <Tab key={index} id={tab.id} classes={{ root: classes.tab }} label={tab.label} />
                                ))}
                            </Tabs>

                            <Box className={classes.tabContainer} sx={{ display: "block" }}>
                                {tab}
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={3} lg={3} mt={2} style={{ width: "328px" }}>
                            {brandLearningPageEnabled && (
                                <Box
                                    sx={{
                                        mb: 1,
                                        p: 2,
                                        boxShadow: 3,
                                        borderRadius: 2,
                                        backgroundColor: theme => theme.palette.info.main
                                    }}
                                >
                                    <Typography variant="h6" fontSize={18} fontWeight={500}>
                                        {t("Brand Learning will help you onboard successfully")}
                                    </Typography>

                                    <Box onClick={handleBrandRulesClick} sx={{ cursor: "pointer" }}>
                                        <Typography variant="body2" className={classes.row2Text} mt={2}>
                                            <Box className={classes.labelBox}>{t("Go to Brand Learning")}</Box>
                                            <ArrowRight
                                                fontSize="small"
                                                sx={{ color: theme => theme.palette.primary.main }}
                                            />
                                            <GraduationCap viewBox="0 0 35 29" className={classes.defaultIcon} />
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            <OnboardingProgressCard
                                brandLearningPageEnabled={brandLearningPageEnabled}
                                onboardingDetail={onboardingDetail?.onboarding}
                            />
                        </Grid>
                    </Grid>
                </OnboardingContainer>
            )}

            {showBrandLearningPage && (
                <ExpertBrandLearningScreen
                    ctaClick={() => setShowBrandLearningPage(false)}
                    content={onboardingBrandLearningContent}
                    showBackButton
                />
            )}

            {message.messageId && message.groupName === groupName && <OnboardingConversation />}
        </>
    );
};

export default OnboardingDetailScreen;
